import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import {
  useAlertCategories,
  useAlertRulesV2,
  useCreateRuleV2,
  useDeleteRuleV2,
  useUpdateRuleV2,
} from "@/hooks/use-alerts";
import type { AlertCategory, AlertRuleV2 } from "@/lib/types";
import { Plus, Trash2 } from "lucide-react";
import { useState } from "react";

// ─── Types ───

type Severity = "critical" | "warning" | "info";
type Delivery = "realtime" | "digest";

type RuleFormState = {
  category: number;
  name: string;
  platform: string;
  severity: Severity;
  delivery_mode: Delivery;
  cooldown_minutes: number;
  enabled: boolean;
  // category-specific config fields
  hours: number;
  count: number;
  window_minutes: number;
  folders: number;
  pct: number;
  deviation_pct: number;
  consecutive_failures: number;
  disk_pct: number;
  daily_gb: number;
};

const EMPTY_FORM: RuleFormState = {
  category: 0,
  name: "",
  platform: "",
  severity: "warning",
  delivery_mode: "realtime",
  cooldown_minutes: 30,
  enabled: true,
  hours: 2,
  count: 10,
  window_minutes: 60,
  folders: 100,
  pct: 20,
  deviation_pct: 15,
  consecutive_failures: 3,
  disk_pct: 85,
  daily_gb: 5,
};

// ─── Helpers ───

const SEV_PILL: Record<Severity, string> = {
  critical: "bg-[var(--iris-neon-pink)]/[0.15] text-[var(--iris-neon-pink)]",
  warning: "bg-[var(--iris-neon-orange)]/[0.15] text-[var(--iris-neon-orange)]",
  info: "bg-[var(--iris-bg-4)] text-[var(--iris-fg-3)]",
};

function buildConfig(slug: string, form: RuleFormState): Record<string, number> {
  switch (slug) {
    case "scraper_stale":
      return { hours: form.hours };
    case "ingestion_stopped":
      return { hours: form.hours };
    case "error_rate_spike":
      return { count: form.count, window_minutes: form.window_minutes };
    case "bucket_backlog":
      return { folders: form.folders };
    case "component_down":
      return {};
    case "scraper_coverage_drop":
      return { pct: form.pct };
    case "baseline_drift":
      return { deviation_pct: form.deviation_pct };
    case "cloud_run_job_failure":
      return { consecutive_failures: form.consecutive_failures };
    case "disk_db_growth":
      return { disk_pct: form.disk_pct, daily_gb: form.daily_gb };
    case "orphan_backlog":
      return { count: form.count, hours: form.hours };
    default:
      return {};
  }
}

function formFromRule(rule: AlertRuleV2): RuleFormState {
  const c = rule.config;
  return {
    category: rule.category,
    name: rule.name,
    platform: rule.platform ?? "",
    severity: rule.severity,
    delivery_mode: rule.delivery_mode,
    cooldown_minutes: rule.cooldown_minutes,
    enabled: rule.enabled,
    hours: (c.hours as number) ?? 2,
    count: (c.count as number) ?? 10,
    window_minutes: (c.window_minutes as number) ?? 60,
    folders: (c.folders as number) ?? 100,
    pct: (c.pct as number) ?? 20,
    deviation_pct: (c.deviation_pct as number) ?? 15,
    consecutive_failures: (c.consecutive_failures as number) ?? 3,
    disk_pct: (c.disk_pct as number) ?? 85,
    daily_gb: (c.daily_gb as number) ?? 5,
  };
}

// ─── Config Fields ───

type ConfigFieldsProps = {
  slug: string;
  form: RuleFormState;
  onChange: (patch: Partial<RuleFormState>) => void;
  inputClass: string;
};

function ConfigFields({ slug, form, onChange, inputClass }: ConfigFieldsProps) {
  switch (slug) {
    case "scraper_stale":
    case "ingestion_stopped":
      return (
        <label className="flex flex-col gap-1">
          <span className="text-[11px] text-[var(--iris-fg-4)]">Stale after (hours)</span>
          <input
            type="number"
            min={1}
            value={form.hours}
            onChange={(e) => onChange({ hours: Number(e.target.value) })}
            className={inputClass}
          />
        </label>
      );
    case "error_rate_spike":
      return (
        <>
          <label className="flex flex-col gap-1">
            <span className="text-[11px] text-[var(--iris-fg-4)]">Error count threshold</span>
            <input
              type="number"
              min={1}
              value={form.count}
              onChange={(e) => onChange({ count: Number(e.target.value) })}
              className={inputClass}
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[11px] text-[var(--iris-fg-4)]">Window (minutes)</span>
            <input
              type="number"
              min={1}
              value={form.window_minutes}
              onChange={(e) => onChange({ window_minutes: Number(e.target.value) })}
              className={inputClass}
            />
          </label>
        </>
      );
    case "bucket_backlog":
      return (
        <label className="flex flex-col gap-1">
          <span className="text-[11px] text-[var(--iris-fg-4)]">Max pending folders</span>
          <input
            type="number"
            min={1}
            value={form.folders}
            onChange={(e) => onChange({ folders: Number(e.target.value) })}
            className={inputClass}
          />
        </label>
      );
    case "scraper_coverage_drop":
      return (
        <label className="flex flex-col gap-1">
          <span className="text-[11px] text-[var(--iris-fg-4)]">Drop threshold (%)</span>
          <input
            type="number"
            min={1}
            max={100}
            value={form.pct}
            onChange={(e) => onChange({ pct: Number(e.target.value) })}
            className={inputClass}
          />
        </label>
      );
    case "baseline_drift":
      return (
        <label className="flex flex-col gap-1">
          <span className="text-[11px] text-[var(--iris-fg-4)]">Deviation threshold (%)</span>
          <input
            type="number"
            min={1}
            max={100}
            value={form.deviation_pct}
            onChange={(e) => onChange({ deviation_pct: Number(e.target.value) })}
            className={inputClass}
          />
        </label>
      );
    case "cloud_run_job_failure":
      return (
        <label className="flex flex-col gap-1">
          <span className="text-[11px] text-[var(--iris-fg-4)]">
            Consecutive failures before alert
          </span>
          <input
            type="number"
            min={1}
            value={form.consecutive_failures}
            onChange={(e) => onChange({ consecutive_failures: Number(e.target.value) })}
            className={inputClass}
          />
        </label>
      );
    case "disk_db_growth":
      return (
        <>
          <label className="flex flex-col gap-1">
            <span className="text-[11px] text-[var(--iris-fg-4)]">Disk usage threshold (%)</span>
            <input
              type="number"
              min={1}
              max={100}
              value={form.disk_pct}
              onChange={(e) => onChange({ disk_pct: Number(e.target.value) })}
              className={inputClass}
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[11px] text-[var(--iris-fg-4)]">Daily growth limit (GB)</span>
            <input
              type="number"
              min={0}
              value={form.daily_gb}
              onChange={(e) => onChange({ daily_gb: Number(e.target.value) })}
              className={inputClass}
            />
          </label>
        </>
      );
    case "orphan_backlog":
      return (
        <>
          <label className="flex flex-col gap-1">
            <span className="text-[11px] text-[var(--iris-fg-4)]">Backlog count threshold</span>
            <input
              type="number"
              min={1}
              name="count"
              value={form.count}
              onChange={(e) => onChange({ count: Number(e.target.value) })}
              className={inputClass}
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[11px] text-[var(--iris-fg-4)]">Min age hours</span>
            <input
              type="number"
              min={1}
              name="hours"
              value={form.hours}
              onChange={(e) => onChange({ hours: Number(e.target.value) })}
              className={inputClass}
            />
          </label>
        </>
      );
    default:
      return null;
  }
}

// ─── Rule Form Dialog ───

type RuleFormDialogProps = {
  open: boolean;
  onClose: () => void;
  categories: AlertCategory[];
  editingRule: AlertRuleV2 | null;
};

function RuleFormDialog({ open, onClose, categories, editingRule }: RuleFormDialogProps) {
  const isEdit = editingRule !== null;
  const createRule = useCreateRuleV2();
  const updateRule = useUpdateRuleV2();

  const firstCategory = categories[0];
  const initialCategory = isEdit ? editingRule.category : (firstCategory?.id ?? 0);

  const [form, setForm] = useState<RuleFormState>(() =>
    isEdit ? formFromRule(editingRule) : { ...EMPTY_FORM, category: initialCategory },
  );

  const patch = (p: Partial<RuleFormState>) => setForm((prev) => ({ ...prev, ...p }));

  function handleCategoryChange(nextId: number) {
    const next = categories.find((c) => c.id === nextId);
    const defaults = next?.default_threshold ?? {};
    setForm((prev) => ({
      ...prev,
      category: nextId,
      hours: typeof defaults.hours === "number" ? defaults.hours : prev.hours,
      count: typeof defaults.count === "number" ? defaults.count : prev.count,
      window_minutes:
        typeof defaults.window_minutes === "number" ? defaults.window_minutes : prev.window_minutes,
      folders: typeof defaults.folders === "number" ? defaults.folders : prev.folders,
      pct: typeof defaults.pct === "number" ? defaults.pct : prev.pct,
      deviation_pct:
        typeof defaults.deviation_pct === "number" ? defaults.deviation_pct : prev.deviation_pct,
      consecutive_failures:
        typeof defaults.consecutive_failures === "number"
          ? defaults.consecutive_failures
          : prev.consecutive_failures,
      disk_pct: typeof defaults.disk_pct === "number" ? defaults.disk_pct : prev.disk_pct,
      daily_gb: typeof defaults.daily_gb === "number" ? defaults.daily_gb : prev.daily_gb,
    }));
  }

  const selectedCategory = categories.find((c) => c.id === form.category);
  const slug = isEdit ? editingRule.category_slug : (selectedCategory?.slug ?? "");

  const inputClass = "iris-input w-full text-[12px]";
  const selectClass = `${inputClass} cursor-pointer`;

  const isPending = createRule.isPending || updateRule.isPending;

  function handleSubmit() {
    const config = buildConfig(slug, form);
    const payload = {
      category: form.category,
      name: form.name,
      platform: form.platform || null,
      config,
      severity: form.severity,
      delivery_mode: form.delivery_mode,
      cooldown_minutes: form.cooldown_minutes,
      enabled: form.enabled,
    };

    if (isEdit) {
      updateRule.mutate({ id: editingRule.id, data: payload }, { onSuccess: onClose });
    } else {
      createRule.mutate(payload, { onSuccess: onClose });
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) onClose();
      }}
    >
      <DialogContent className="max-w-[480px]">
        <DialogTitle>{isEdit ? "Edit Rule" : "New Rule"}</DialogTitle>
        <DialogDescription>
          {isEdit ? `Editing "${editingRule.category_name}" rule` : "Configure a new alerting rule"}
        </DialogDescription>

        <div className="flex flex-col gap-3 pt-1">
          {/* Category — create only */}
          {!isEdit && (
            <label className="flex flex-col gap-1">
              <span className="text-[11px] text-[var(--iris-fg-4)]">Category</span>
              <select
                value={form.category}
                onChange={(e) => handleCategoryChange(Number(e.target.value))}
                className={selectClass}
              >
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </select>
              {selectedCategory?.description && (
                <span className="text-[11px] text-[var(--iris-fg-4)]">
                  {selectedCategory.description}
                </span>
              )}
            </label>
          )}

          {/* Name */}
          <label className="flex flex-col gap-1">
            <span className="text-[11px] text-[var(--iris-fg-4)]">Rule name</span>
            <input
              value={form.name}
              onChange={(e) => patch({ name: e.target.value })}
              placeholder="e.g. Telegram scraper stale"
              className={inputClass}
            />
          </label>

          {/* Platform — only for categories with supports_platform */}
          {(isEdit ? editingRule.supports_platform : selectedCategory?.supports_platform) && (
            <label className="flex flex-col gap-1">
              <span className="text-[11px] text-[var(--iris-fg-4)]">Platform</span>
              <input
                value={form.platform}
                onChange={(e) => patch({ platform: e.target.value })}
                placeholder="e.g. telegram"
                className={inputClass}
              />
            </label>
          )}

          {/* Category-specific config fields */}
          <ConfigFields slug={slug} form={form} onChange={patch} inputClass={inputClass} />

          {/* Common fields */}
          <div className="grid grid-cols-2 gap-3">
            <label className="flex flex-col gap-1">
              <span className="text-[11px] text-[var(--iris-fg-4)]">Severity</span>
              <select
                value={form.severity}
                onChange={(e) => patch({ severity: e.target.value as Severity })}
                className={selectClass}
              >
                <option value="critical">critical</option>
                <option value="warning">warning</option>
                <option value="info">info</option>
              </select>
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-[11px] text-[var(--iris-fg-4)]">Delivery</span>
              <select
                value={form.delivery_mode}
                onChange={(e) => patch({ delivery_mode: e.target.value as Delivery })}
                className={selectClass}
              >
                <option value="realtime">realtime</option>
                <option value="digest">digest</option>
              </select>
            </label>
          </div>

          <label className="flex flex-col gap-1">
            <span className="text-[11px] text-[var(--iris-fg-4)]">Cooldown (minutes)</span>
            <input
              type="number"
              min={1}
              value={form.cooldown_minutes}
              onChange={(e) => patch({ cooldown_minutes: Number(e.target.value) })}
              className={inputClass}
            />
          </label>

          <label className="flex cursor-pointer items-center gap-2.5">
            <input
              type="checkbox"
              checked={form.enabled}
              onChange={(e) => patch({ enabled: e.target.checked })}
              className="h-4 w-4 rounded accent-[var(--iris-neon-purple)]"
            />
            <span className="text-[12px] text-[var(--iris-fg-3)]">Enabled</span>
          </label>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" className="iris-btn" onClick={onClose}>
              Cancel
            </button>
            <button
              type="button"
              className="iris-btn iris-btn--primary"
              disabled={!form.name || isPending}
              onClick={handleSubmit}
            >
              {isEdit ? "Save changes" : "Create rule"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Main Component ───

export function RuleEditor() {
  const { data: rules } = useAlertRulesV2();
  const { data: categories } = useAlertCategories();
  const deleteRule = useDeleteRuleV2();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<AlertRuleV2 | null>(null);

  const ruleList = rules ?? [];
  const catList = categories ?? [];

  // Group rules by category_name
  const grouped = ruleList.reduce<Record<string, AlertRuleV2[]>>((acc, rule) => {
    const key = rule.category_name;
    if (!acc[key]) acc[key] = [];
    acc[key].push(rule);
    return acc;
  }, {});

  function openCreate() {
    setEditingRule(null);
    setDialogOpen(true);
  }

  function openEdit(rule: AlertRuleV2) {
    setEditingRule(rule);
    setDialogOpen(true);
  }

  function handleDelete(rule: AlertRuleV2) {
    if (confirm(`Delete rule "${rule.name}"?`)) {
      deleteRule.mutate(rule.id);
    }
  }

  function closeDialog() {
    setDialogOpen(false);
    setEditingRule(null);
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <span className="text-[13px] text-[var(--iris-fg-3)]">
          {ruleList.length} {ruleList.length === 1 ? "rule" : "rules"} configured
        </span>
        <button type="button" className="iris-btn" onClick={openCreate}>
          <Plus size={13} />
          New Rule
        </button>
      </div>

      {/* Empty state */}
      {Object.keys(grouped).length === 0 && (
        <div className="overflow-hidden rounded-[16px] border border-[var(--iris-line-2)] bg-[var(--iris-bg-2)]">
          <div className="px-[18px] py-4 text-[12px] text-[var(--iris-fg-3)]">
            No rules configured. Click "New Rule" to get started.
          </div>
        </div>
      )}

      {/* Grouped rule list */}
      {Object.entries(grouped).map(([categoryName, categoryRules]) => (
        <div
          key={categoryName}
          className="overflow-hidden rounded-[16px] border border-[var(--iris-line-2)] bg-[var(--iris-bg-2)]"
        >
          {/* Category header */}
          <div className="border-b border-[var(--iris-line-1)] px-[18px] py-2.5">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-[var(--iris-fg-4)]">
              {categoryName}
            </span>
          </div>

          {/* Column headers */}
          <div className="grid grid-cols-[2fr_1fr_80px_70px_48px] gap-1 border-b border-[var(--iris-line-1)] px-[18px] py-2 text-[11px] text-[var(--iris-fg-4)]">
            <span>Name</span>
            <span>Platform</span>
            <span>Severity</span>
            <span>Delivery</span>
            <span />
          </div>

          {categoryRules.map((rule) => (
            <div
              key={rule.id}
              className="grid grid-cols-[2fr_1fr_80px_70px_48px] items-center gap-1 border-b border-[var(--iris-line-1)] text-[12px] last:border-0"
            >
              {/* Clickable row area (all columns except delete) */}
              <button
                type="button"
                onClick={() => openEdit(rule)}
                className="col-span-4 grid cursor-pointer grid-cols-[2fr_1fr_80px_70px] items-center gap-1 px-[18px] py-3 text-left transition-colors hover:bg-[var(--iris-bg-4)]/50"
              >
                {/* Name + enabled dot */}
                <span className="flex items-center gap-2">
                  <span
                    className={`h-[7px] w-[7px] shrink-0 rounded-full ${rule.enabled ? "bg-[var(--iris-neon-green)]" : "bg-[var(--iris-bg-4)]"}`}
                  />
                  <span className={rule.enabled ? "" : "text-[var(--iris-fg-4)]"}>{rule.name}</span>
                </span>

                {/* Platform */}
                <span className="text-[var(--iris-fg-3)]">
                  {rule.platform ?? <span className="text-[var(--iris-fg-4)]">—</span>}
                </span>

                {/* Severity pill */}
                <span>
                  <span
                    className={`rounded-[6px] px-2 py-0.5 text-[10px] font-medium ${SEV_PILL[rule.severity]}`}
                  >
                    {rule.severity}
                  </span>
                </span>

                {/* Delivery */}
                <span className="text-[11px] text-[var(--iris-fg-3)]">{rule.delivery_mode}</span>
              </button>

              {/* Delete — separate cell, not inside the edit button */}
              <div className="flex items-center justify-center py-3 pr-2">
                <button
                  type="button"
                  className="iris-btn"
                  style={{
                    padding: 4,
                    color: "var(--iris-fg-4)",
                    background: "transparent",
                    border: "none",
                  }}
                  disabled={deleteRule.isPending}
                  onClick={() => handleDelete(rule)}
                >
                  <Trash2 size={13} />
                </button>
              </div>
            </div>
          ))}
        </div>
      ))}

      {/* Dialog */}
      {dialogOpen && (
        <RuleFormDialog
          open={dialogOpen}
          onClose={closeDialog}
          categories={catList}
          editingRule={editingRule}
        />
      )}
    </div>
  );
}
